import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { DemoService } from '../../../../demo.service';
import { StaffWeightedWorkReport, StaffWeightedWorkSearch, WeightedWorkSnapshot } from '../../../../shared/models/staff-weighted-work.model';
import { benchmarkIsActive, STAFF_ACTIVE_BENCHMARKS_UPDATE_MESSAGE } from '../../../../shared/models/benchmark-policy.model';
import { staffExclusionsMatch, STAFF_EXCLUSIONS_UPDATE_MESSAGE } from '../../../../shared/models/staff-exclusions.model';
import { staffCalendarMatches, STAFF_CALENDAR_UPDATE_MESSAGE } from '../../../../shared/models/staff-reporting-calendar.model';

@Component({
  selector: 'app-staff-weighted-work-live',
  template: `<div class="ibox"><div class="ibox-title"><h5>Weighted Work Minutes: action filter</h5></div><div class="ibox-content">
    <p>Uses the shared dates, staff selection, staff exclusions and weekend-target setting.</p>
    <label><input type="checkbox" [(ngModel)]="allActions" (ngModelChange)="reload()"> Include all eligible actions in Weighted Work Minutes</label>
    <p class="text-muted">Applies only to the Weighted Work Minutes charts and tables below. Eligible actions have an active staff benchmark and minutes per action above 0. Uncheck to select specific actions.</p>
    <ejs-multiselect *ngIf="!allActions" [dataSource]="availableActions" [fields]="actionFields" [(ngModel)]="selectedActions"
      placeholder="Select weighted actions" [mode]="'Box'" (change)="selectActions($event.value)"></ejs-multiselect>
    <p *ngIf="actionError" class="text-warning">{{actionError}}</p>
    <button type="button" class="btn btn-default btn-sm" (click)="refresh()">Refresh weighted work</button>
  </div></div>
  <app-staff-weighted-work [report]="report" [loading]="loading" [error]="error"></app-staff-weighted-work>`
})
export class StaffWeightedWorkLiveComponent implements OnChanges, OnDestroy {
  @Input() search: StaffWeightedWorkSearch | null = null;
  report: StaffWeightedWorkReport | null = null;
  loading = false;
  error = '';
  actionError = '';
  allActions = true;
  selectedActions: number[] = [];
  availableActions: Array<{ log_type_id: number; log_type_name: string }> = [];
  actionFields = { value: 'log_type_id', text: 'log_type_name' };
  private request: Subscription;
  private actionsRequest: Subscription;
  private generation = 0;
  private appliedSearch = '';

  constructor(private api: DemoService) { this.loadActions(); }
  ngOnChanges(changes: SimpleChanges): void { if (changes.search) { this.reload(); } }
  ngOnDestroy(): void { this.generation++; this.request?.unsubscribe(); this.actionsRequest?.unsubscribe(); }
  refresh(): void { this.loadActions(); this.reload(); }
  selectActions(ids: Array<number | string> | null): void { this.selectedActions = (ids || []).map(Number); this.reload(); }
  private loadActions(): void {
    this.actionsRequest?.unsubscribe();
    this.actionsRequest = this.api.getSystemLogTypesWithBenchmarksStaff().subscribe({
      next: (rows: any[]) => {
        const byId = new Map<number, { log_type_id: number; log_type_name: string }>();
        rows.filter(row => benchmarkIsActive(row.is_active) && Number(row.unit_time_duration_ms) > 0).forEach(row => byId.set(Number(row.log_type_id), {
          log_type_id: Number(row.log_type_id), log_type_name: row.log_type_name
        }));
        this.availableActions = Array.from(byId.values()); this.actionError = '';
      },
      error: () => { this.actionError = 'The action list could not be loaded. Refresh to try again.'; }
    });
  }
  reload(): void {
    const generation = ++this.generation;
    this.request?.unsubscribe(); this.report = null; this.error = ''; this.loading = false;
    if (!this.search?.when_from || !this.search?.when_to) { return; }
    const search = this.requestFilters(this.search);
    this.appliedSearch = JSON.stringify(search);
    this.loading = true;
    this.request = this.api.fetchStaffWeightedWork(search).subscribe({
      next: report => {
        if (generation !== this.generation) { return; }
        this.loading = false;
        if (!staffCalendarMatches(report?.context, search)) { this.error = STAFF_CALENDAR_UPDATE_MESSAGE; return; }
        if (!staffExclusionsMatch(report?.context, search)) { this.error = STAFF_EXCLUSIONS_UPDATE_MESSAGE; this.loading = false; return; }
        if (report && report.context?.benchmark_policy !== 'active_only') { this.error = STAFF_ACTIVE_BENCHMARKS_UPDATE_MESSAGE; this.loading = false; return; }
        this.report = report;
      },
      error: failure => {
        if (generation !== this.generation) { return; }
        const detail = failure.error?.error;
        this.error = detail?.message || 'Weighted work is unavailable. Refresh to retry.';
        if (detail?.details?.length) {
          this.error += ' ' + detail.details.map(row => 'Action ' + row.log_type_id + ': benchmark IDs ' + row.benchmark_ids.join(', ')).join('; ');
        }
        this.loading = false;
      }
    });
  }
  private requestFilters(search: StaffWeightedWorkSearch): StaffWeightedWorkSearch {
    const result: StaffWeightedWorkSearch = {
      when_from: search.when_from, when_to: search.when_to,
      weighted_log_type_ids: this.allActions ? null : [...this.selectedActions]
    };
    for (const field of ['include_weekends', 'excluded_staff_ids', 'created_by', 'created_by_group', 'brand_id', 'brand_report_id', 'brand_type_id', 'created_level', 'subject_id', 'subject_number', 'job_id', 'ip_address']) {
      if (search[field] !== undefined && search[field] !== null && search[field] !== '') { result[field] = search[field]; }
    }
    return result;
  }
  snapshot(currentSearch: StaffWeightedWorkSearch): WeightedWorkSnapshot {
    if (this.report && !this.loading && !this.error && JSON.stringify(this.requestFilters(currentSearch)) === this.appliedSearch) {
      return JSON.parse(JSON.stringify(this.report));
    }
    return { version: 1, status: 'unavailable', reason: this.error || 'Weighted results were not ready for these filters when saved.' };
  }
}
